import { useEffect, useState } from "react";
import EmojiPicker from "emoji-picker-react";

import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  increment,
  onSnapshot
} from "firebase/firestore";

import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

import {
  Timer,
  Play,
  Square,
  ChevronDown,
  Smile
} from "lucide-react";

export default function TimeTracker() {

  const { user, orgId } = useAuth();

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const [desc, setDesc] = useState("");
  const [progress, setProgress] = useState("");

  const [showEmoji, setShowEmoji] = useState(false);

  /* ===================================================== */
  /* 😀 EMOJI */
  /* ===================================================== */

  const onEmojiClick = (emojiData) => {

    setDesc(prev => prev + emojiData.emoji);

    setShowEmoji(false);

  };

  /* ===================================================== */
  /* 🔥 RESTORE TIMER */
  /* ===================================================== */

  useEffect(() => {

    const savedTimer = localStorage.getItem("runningTimer");

    if (savedTimer) {

      try {

        const timerData = JSON.parse(savedTimer);

        if (timerData?.startTime) {

          setStartTime(timerData.startTime);

          setSelectedProject(timerData.project);

          setIsRunning(true);

        }

      } catch (error) {

        console.error("Timer restore error:", error);

      }

    }

  }, []);

  /* ===================================================== */
  /* 📦 FETCH PROJECTS */
  /* ===================================================== */

  useEffect(() => {

    if (!orgId || !user?.uid) return;

    const unsub = onSnapshot(
      collection(db, "organizations", orgId, "projects"),
      (snap) => {

        const data = snap.docs
          .map(d => ({
            id: d.id,
            ...d.data()
          }))
          .filter(
            p => p.assignedEmployees?.includes(user.uid)
          );

        setProjects(data);

      }
    );

    return () => unsub();

  }, [orgId, user]);

  /* ===================================================== */
  /* ⏱ TIMER */
  /* ===================================================== */

  useEffect(() => {

    let interval;

    if (isRunning && startTime) {

      interval = setInterval(() => {

        const seconds = Math.floor(
          (Date.now() - startTime) / 1000
        );

        setElapsed(seconds);

      }, 1000);

    }

    return () => clearInterval(interval);

  }, [isRunning, startTime]);

  /* ===================================================== */
  /* 💾 SAVE TIMER BEFORE PAGE CLOSE */
  /* ===================================================== */

  useEffect(() => {

    const handleBeforeUnload = () => {

      if (isRunning && startTime) {

        localStorage.setItem(
          "runningTimer",
          JSON.stringify({
            startTime,
            project: selectedProject
          })
        );

      }

    };

    window.addEventListener(
      "beforeunload",
      handleBeforeUnload
    );

    return () => {

      window.removeEventListener(
        "beforeunload",
        handleBeforeUnload
      );

    };

  }, [isRunning, startTime, selectedProject]);

  /* ===================================================== */
  /* ▶ START WORK */
  /* ===================================================== */

  const startWork = () => {

    if (!selectedProject) {

      alert("Please select a project");

      return;

    }

    const now = Date.now();

    setStartTime(now);

    setElapsed(0);

    setIsRunning(true);

    // ✅ SAVE TIMER
    localStorage.setItem(
      "runningTimer",
      JSON.stringify({
        startTime: now,
        project: selectedProject
      })
    );

  };

  /* ===================================================== */
  /* ⏹ STOP WORK */
  /* ===================================================== */

  const stopWork = async () => {

    if (!selectedProject || !startTime) return;

    const duration = Math.floor(
      (Date.now() - startTime) / 1000
    );

    if (!desc.trim()) {

      alert("Please enter work description");

      return;

    }

    if (progress > 100) {

      alert("Progress cannot exceed 100%");

      return;

    }

    try {

      /* ========================================= */
      /* SAVE TIME LOG */
      /* ========================================= */

      await addDoc(
        collection(
          db,
          "organizations",
          orgId,
          "timeLogs"
        ),
        {
          employeeId: user.uid,

          employeeName:
            user.displayName || user.email,

          projectId: selectedProject.id,

          projectName:
            selectedProject.clientName,

          clientName:
            selectedProject.clientName,

          description: desc,

          progress: Number(progress),

          duration,

          createdAt: serverTimestamp()
        }
      );

      /* ========================================= */
      /* UPDATE PROJECT */
      /* ========================================= */

      const projectRef = doc(
        db,
        "organizations",
        orgId,
        "projects",
        selectedProject.id
      );

      const currentProgress =
        selectedProject.progress || 0;

      let newProgress =
        currentProgress + Number(progress);

      if (newProgress > 100) {

        newProgress = 100;

      }

      await updateDoc(projectRef, {

        totalWorkedSeconds:
          increment(duration),

        progress: newProgress

      });

      /* ========================================= */
      /* RESET */
      /* ========================================= */

      setIsRunning(false);

      setStartTime(null);

      setElapsed(0);

      setDesc("");

      setProgress("");

      // ✅ CLEAR STORAGE
      localStorage.removeItem("runningTimer");

      alert("Work log saved successfully");

    } catch (error) {

      console.error(
        "Time tracking error:",
        error
      );

      alert("Failed to save work log");

    }

  };

  /* ===================================================== */
  /* ⏰ FORMAT TIME */
  /* ===================================================== */

  const formatTime = (s) => {

    const h = Math.floor(s / 3600);

    const m = Math.floor((s % 3600) / 60);

    const sec = s % 60;

    return `${h
      .toString()
      .padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;

  };

  /* ===================================================== */
  /* UI */
  /* ===================================================== */

  return (

    <div className="max-w-5xl mx-auto px-6 py-10">

      {/* HEADER */}

      <div className="flex items-center gap-3 mb-8">

        <Timer className="text-indigo-600" />

        <h1 className="text-2xl font-bold">
          Time Tracker
        </h1>

      </div>

      {/* MAIN CARD */}

      <div className="bg-white p-8 rounded-2xl shadow-lg border">

        <div className="grid md:grid-cols-2 gap-10">

          {/* LEFT */}

          <div className="space-y-6">

            {/* PROJECT */}

            <div>

              <label className="text-sm font-semibold mb-2 block">

                Select Project

              </label>

              <div className="relative">

                <select
                  disabled={isRunning}
                  value={selectedProject?.id || ""}
                  onChange={(e) => {

                    const project = projects.find(
                      p => p.id === e.target.value
                    );

                    setSelectedProject(project);

                  }}
                  className="w-full border rounded-xl p-4 pr-10 appearance-none"
                >

                  <option value="">
                    Select project...
                  </option>

                  {projects.map((p) => (

                    <option
                      key={p.id}
                      value={p.id}
                    >
                      {p.clientName}
                    </option>

                  ))}

                </select>

                <ChevronDown
                  size={18}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

              </div>

            </div>

            {/* DESCRIPTION */}

            <div className="relative">

              <textarea
                placeholder="Work description..."
                value={desc}
                onChange={(e) =>
                  setDesc(e.target.value)
                }
                className="w-full border rounded-xl p-4 pr-10 text-justify leading-relaxed"
                rows={4}
              />

              {/* EMOJI BUTTON */}

              <button
                type="button"
                onClick={() =>
                  setShowEmoji(!showEmoji)
                }
                className="absolute right-3 top-3 text-gray-500 hover:text-indigo-600"
              >

                <Smile size={18} />

              </button>

              {/* EMOJI PICKER */}

              {showEmoji && (

                <div className="absolute right-0 mt-2 z-50">

                  <EmojiPicker
                    onEmojiClick={onEmojiClick}
                  />

                </div>

              )}

            </div>

            {/* PROGRESS */}

            <div>

              <label className="text-sm font-semibold">

                Completion %

              </label>

              <input
                type="number"
                min="0"
                max="100"
                value={progress}
                onChange={(e) =>
                  setProgress(e.target.value)
                }
                className="w-full border rounded-xl p-3 mt-2"
              />

              <div className="mt-3 h-3 bg-gray-200 rounded-full overflow-hidden">

                <div
                  className="bg-indigo-600 h-full transition-all"
                  style={{
                    width: `${progress || 0}%`
                  }}
                />

              </div>

            </div>

          </div>

          {/* RIGHT TIMER */}

          <div className="flex flex-col items-center justify-center bg-gray-50 rounded-2xl p-10">

            <div className="text-5xl font-mono mb-6">

              {formatTime(elapsed)}

            </div>

            {!isRunning ? (

              <button
                onClick={startWork}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl flex gap-2 transition-all"
              >

                <Play size={18} />

                Start

              </button>

            ) : (

              <button
                onClick={stopWork}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl flex gap-2 transition-all"
              >

                <Square size={18} />

                Stop

              </button>

            )}

          </div>

        </div>

      </div>

    </div>

  );

}